import { useInfiniteQuery } from "@tanstack/react-query";
import { tutorApiService } from "../../services/tutorApi";
import { TutorGroup } from "../../types/tutor.types";
import { Mode } from "../../types";
import { useUserQuery } from "./useUserQuery";

/**
 * useInfiniteStorageListQuery
 *
 * React Query의 useInfiniteQuery를 사용한 보관함 목록 무한 스크롤
 *
 * 기능:
 * - 페이지네이션 자동 관리
 * - 스크롤 시 자동 로드
 * - 캐싱 및 중복 방지
 *
 * 사용법:
 * const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteStorageListQuery({ mode });
 */

interface UseInfiniteStorageListQueryOptions {
  mode: Mode;
  limit?: number;
}

interface StoragePage {
  storage: TutorGroup[];
  nextPage: number;
  hasMore: boolean;
}

const TUTOR_URLS = {
  STORAGE_LIST: process.env.REACT_APP_TUTOR_GET_STORAGE_LIST,
};

const SOCRATES_URLS = {
  STORAGE_LIST: process.env.REACT_APP_SOCRATES_GET_STORAGE_LIST,
};

export const useInfiniteStorageListQuery = (
  options: UseInfiniteStorageListQueryOptions
) => {
  const { mode, limit = 50 } = options;

  // 로그인 상태에서만 보관함 목록을 조회 (미로그인/세션만료 시 401 방지)
  const { isLoggedIn } = useUserQuery();

  return useInfiniteQuery<StoragePage>({
    queryKey: ["storage", mode, "infinite"],
    enabled: isLoggedIn,
    queryFn: async ({ pageParam = 1 }) => {
      const url =
        mode === Mode.SOCRATES
          ? SOCRATES_URLS.STORAGE_LIST
          : TUTOR_URLS.STORAGE_LIST;

      const data = await tutorApiService.touchAPI(
        "GET",
        url,
        `?offset=${pageParam}&limit=${limit}`
      );

      if (!data) {
        throw new Error("Failed to fetch storage list");
      }

      const storage = data.storage_list.map((item: TutorGroup) => ({
        ...item,
        isClicked: false,
        isEdit: false,
      }));

      const hasMore = (pageParam as number) * limit < data.total_count;

      return {
        storage,
        nextPage: (pageParam as number) + 1,
        hasMore,
      };
    },
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextPage : undefined),
    initialPageParam: 1,
    staleTime: 30000, // 30초 동안 fresh 상태 유지
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
};
